// src/pages/organization/team/AssignSupervisor.js

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProfilePicture, { default as ProfileImage } from "../../../components/profileImage";
import { useMainPageTitle } from "../../../contexts/PageContext";

// Role definitions (easy to update)
const ROLE_GROUPS = {
  assignSupervisor: ["create", "update", "delete"],
  performance: ["create", "read", "delete"],
  playerRequest: ["update"],
  event: ["create", "update", "delete"],
  eventAttend: ["create", "update", "delete"],
  eventActivity: ["create", "update", "delete"],
};

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


function getDefaultRoles() {
  return Object.fromEntries(
    Object.entries(ROLE_GROUPS).map(([group, perms]) => [
      group,
      Object.fromEntries(perms.map((p) => [p, false])),
    ])
  );
}

// Helper function to get permission descriptions
function getPermissionDescription(group, perm) {
  const descriptions = {
    assignSupervisor: {
      create: "Assign supervisor to team and set their role",
      update: "Update supervisor role",
      delete: "Remove supervisor from team",
    },
    performance: {
      create: "Add new performance records",
      read: "View performance statistics",
      delete: "Remove performance records",
    },
    playerRequest: {
      update: "Approve or reject player requests",
    },
    event: {
      create: "Create new team events",
      update: "Modify existing events",
      delete: "Remove events from calendar",
    },
    eventAttend: {
      create: "Mark attendance for events",
      update: "Update attendance records",
      delete: "Remove attendance records",
    },
    eventActivity: {
      create: "Add activities to events",
      update: "Modify event activities",
      delete: "Remove activities from events",
    },
  };

  return descriptions[group]?.[perm] || `Permission to ${perm} ${group}`;
}

const AssignSupervisor = () => {
  const { teamId, memberId } = useParams();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();

  // State
  const [team, setTeam] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedSupervisor, setSelectedSupervisor] = useState("");
  const [title, setTitle] = useState("");
  const [roles, setRoles] = useState(getDefaultRoles());
  const [selectedSupervisorData, setSelectedSupervisorData] = useState(null);

  const isEditMode = Boolean(memberId);

  // Set page title
  useEffect(() => {
    updatePageTitle(
      isEditMode ? "Edit Supervisor Role" : "Assign Supervisor",
      isEditMode
        ? "Update supervisor permissions for your team"
        : "Assign a supervisor to a team"
    );
  }, [updatePageTitle, isEditMode]);

  // Fetch all required data in parallel
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Requests
      const teamReq = fetch(
        `${API_URL}/team/show-team-by-team-id?teamId=${teamId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const supReq = fetch(`${API_URL}/supervisor/get-all-supervisors`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // Add supervisor data fetch if edit mode
      const editSupReq = isEditMode
        ? fetch(
            `${API_URL}/team/show-supervisor?supervisorId=${memberId}&teamId=${teamId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
        : null;

      const [teamRes, supervisorsRes, editSupRes] = await Promise.all([
        teamReq,
        supReq,
        editSupReq,
      ]);

      // Team
      if (!teamRes.ok) throw new Error("Failed to fetch team data");
      const teamData = await teamRes.json();
      setTeam(teamData.team);

      // Supervisors
      if (!supervisorsRes.ok) throw new Error("Failed to fetch supervisors");
      const supervisorsData = await supervisorsRes.json();
      const supervisorsList = supervisorsData.data?.supervisors || supervisorsData.supervisors || supervisorsData;
      setSupervisors(Array.isArray(supervisorsList) ? supervisorsList : []);

      // Edit mode supervisor details
      if (isEditMode && editSupRes && editSupRes.ok) {
        const supData = await editSupRes.json();
        setSelectedSupervisor(supData.supervisor._id);

        console.log("Edit supervisor data:", supData.supervisor.metaData.teams);
        const teamSup = supData.supervisor.metaData.teams.find(
          (team) => team.teamId === teamId
        );

        setTitle(teamSup?.title || supData.supervisor.title || "");

        // Merge permissions
        const roleSource = teamSup?.role || supData.teamRole || {};
        setRoles((prev) =>
          Object.fromEntries(
            Object.entries(prev).map(([group, perms]) => [
              group,
              Object.fromEntries(
                Object.keys(perms).map((key) => [
                  key,
                  !!roleSource[group]?.[key],
                ])
              ),
            ])
          )
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, memberId, isEditMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update selected supervisor data when selection changes
  useEffect(() => {
    if (selectedSupervisor && supervisors.length > 0) {
      const supervisorData = supervisors.find(
        (sup) => sup._id === selectedSupervisor
      );
      setSelectedSupervisorData(supervisorData);
    } else {
      setSelectedSupervisorData(null);
    }
  }, [selectedSupervisor, supervisors]);

  // Update the handleRoleChange function to accept an optional value parameter
  const handleRoleChange = (group, perm, value) => {
    setRoles((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [perm]: value !== undefined ? value : !prev[group][perm],
      },
    }));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedSupervisor) return setError("Please select a supervisor.");

    setLoading(true);
    try {
      const endpoint = isEditMode
        ? "/team/edit-supervisor-role"
        : "/team/assign-supervisor-to-team";
      const response = await fetch(API_URL + endpoint, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          teamId,
          supervisorId: selectedSupervisor,
          title,
          roles,
        }),
      });
      const data = await response.json();

      if (data.message) {
        setSuccess(data.message || "Operation successful");
        setTimeout(
          () =>
            navigate(`/organization/team/${teamId}/member/${selectedSupervisor}`),
          1200
        );
      } else {
        throw new Error(data.message || "Operation failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // UI for loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F15213] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md flex items-start">
          <svg
            className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded-md flex items-start">
          <svg
            className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>{success}</span>
        </div>
      )}

      {/* Team Info Card */}
      {team && (
        <div className="mb-8 overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-md">
          <div className="flex flex-col md:flex-row items-center p-6">
            <div className="relative mb-4 md:mb-0 md:mr-6">
              <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-white shadow-md">
                <ProfileImage
                  image={team.logo}
                  alt={team.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="text-center md:text-left flex-1">
              <h2 className="text-2xl font-bold text-gray-800">{team.name}</h2>
              <p className="text-gray-500 mb-2">@{team.uniqueName}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {team.type}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                  {team.gender}
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                  {team.sport}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800">
              {isEditMode ? "Edit Supervisor Role" : "Assign Supervisor"}
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              {isEditMode
                ? "Update the supervisor's permissions and title for this team"
                : "Select a supervisor and define their permissions for this team"}
            </p>
          </div>

          <div className="p-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Supervisor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supervisor
                </label>
                <select
                  value={selectedSupervisor}
                  onChange={(e) => setSelectedSupervisor(e.target.value)}
                  disabled={isEditMode}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#F15213] focus:ring focus:ring-[#F15213] focus:ring-opacity-20 transition-all"
                  required
                >
                  <option value="">-- Select Supervisor --</option>
                  {supervisors.map((sup) => (
                    <option key={sup._id} value={sup._id}>
                      {sup.firstName} {sup.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#F15213] focus:ring focus:ring-[#F15213] focus:ring-opacity-20 transition-all"
                  placeholder="e.g. Head Coach, Assistant Coach, Team Manager"
                  required
                />
              </div>
            </div>

            {/* Selected Supervisor Card */}
            {selectedSupervisorData && (
              <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                <div className="p-4 flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <ProfilePicture
                      image={selectedSupervisorData.profilePicture}
                      alt={`${selectedSupervisorData.firstName} ${selectedSupervisorData.lastName}`}
                      className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-sm"
                      myUser={selectedSupervisorData}
                    />
                  </div>

                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">
                      {selectedSupervisorData.firstName}{" "}
                      {selectedSupervisorData.lastName}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {selectedSupervisorData.email}
                    </p>
                    {selectedSupervisorData.phone && (
                      <p className="text-sm text-gray-500">
                        {selectedSupervisorData.phone}
                      </p>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      Supervisor
                    </span>
                  </div>
                </div>

                {selectedSupervisorData.metaData?.teams?.length > 0 && (
                  <div className="border-t border-gray-200 px-4 py-3">
                    <h5 className="text-xs font-medium text-gray-500 mb-2">
                      CURRENT TEAMS
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedSupervisorData.metaData.teams.map(
                        (teamData, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs"
                          >
                            {teamData.teamName || "Unknown Team"}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Permissions Section */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800">
              Assign Role & Permissions
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Define what actions the supervisor can perform within this team
            </p>
          </div>

          <div className="p-6">
            {/* Permission Groups */}
            {Object.entries(roles).map(([group, perms]) => (
              <div key={group} className="mb-6 last:mb-0">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-800 capitalize flex items-center">
                    {/* Group Icon based on type */}
                    {group === "assignSupervisor" && (
                      <svg
                        className="w-5 h-5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {group === "performance" && (
                      <svg
                        className="w-5 h-5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 3a1 1 0 000 2h10a1 1 0 100-2H3zm0 4a1 1 0 000 2h6a1 1 0 100-2H3zm0 4a1 1 0 100 2h6a1 1 0 100-2H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {group === "playerRequest" && (
                      <svg
                        className="w-5 h-5 mr-2 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    )}
                    {group === "event" && (
                      <svg
                        className="w-5 h-5 mr-2 text-purple-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {group === "eventAttend" && (
                      <svg
                        className="w-5 h-5 mr-2 text-yellow-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {group === "eventActivity" && (
                      <svg
                        className="w-5 h-5 mr-2 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {group.replace(/([A-Z])/g, " $1").trim()}
                  </h4>

                  {/* Select All Checkbox */}
                  <div>
                    <label className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm">
                      <input
                        type="checkbox"
                        className="rounded text-[#F15213] focus:ring-[#F15213] mr-2"
                        checked={Object.values(perms).every((v) => v)}
                        onChange={() => {
                          const allChecked = Object.values(perms).every(
                            (v) => v
                          );
                          const newValue = !allChecked;
                          Object.keys(perms).forEach((perm) => {
                            handleRoleChange(group, perm, newValue);
                          });
                        }}
                      />
                      <span>Select All</span>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
                    {Object.entries(perms).map(([perm, val]) => (
                      <label
                        key={perm}
                        className={`flex items-center space-x-3 p-3 border-b border-r border-gray-200 hover:bg-gray-100 transition-colors ${
                          val ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={val}
                          onChange={() => handleRoleChange(group, perm)}
                          className="rounded text-[#F15213] focus:ring-[#F15213] h-5 w-5"
                        />
                        <div>
                          <span className="capitalize text-gray-800 font-medium">
                            {perm}
                          </span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {getPermissionDescription(group, perm)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/organization/team/${teamId}`)}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="px-6 py-3 bg-[#F15213] text-white rounded-lg hover:bg-[#F15213]/90 font-medium transition-colors shadow-md hover:shadow-lg flex items-center"
          >
            <span>
              {isEditMode ? "Update Supervisor" : "Assign Supervisor"}
            </span>
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignSupervisor;
